import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import { FGrid, FGridMoneyCell } from '../grid';
import DeterminantReportPanel from './DeterminantReportPanel';
import DeterminantBudgetManager from './DeterminantBudgetManager';
import {
  acceptDeterminantBudget,
  fetchDeterminantBudgetManager,
  fetchDeterminantBudgetWorkbench,
  releaseDeterminantBudget,
  returnDeterminantBudget,
  saveManagementAdjustment,
  validateDeterminantBudget,
} from './determinantBudget.apis';
import {
  DeterminantBudgetTabCode,
  IDeterminantBudgetManager,
  IDeterminantBudgetManagerRow,
  IDeterminantBudgetWorkbench,
} from './determinantBudget.models';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import FPlanningJourney from '../core/FPlanningJourney';

type State = {
  tab: DeterminantBudgetTabCode;
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  manager: IDeterminantBudgetManager | null;
  workbench: IDeterminantBudgetWorkbench | null;
  adjustmentamount: string;
  reserveamount: string;
  adjustmentreason: string;
  notes: string;
};

class DeterminantBudgetWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    tab: 'summary',
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    manager: null,
    workbench: null,
    adjustmentamount: '0',
    reserveamount: '0',
    adjustmentreason: '',
    notes: '',
  };

  componentDidMount() {
    this.initialize();
  }

  private getPageParam = (key: string): unknown => {
    const params: any = this.props.params ?? {};
    return params[key] ?? params?.get?.(key) ?? undefined;
  };

  private getPlanningCycleId = (): number => {
    //const p = (this.props.params || {}) as any;
    //return Number(p.planningcycleid ?? p.pci ?? p.planningCycleId ?? 0);
    const value = this.props.plancontext?.pci;
    return Number(value || 0);
  };

  private initialize = async () => {
    const planningcycleid = this.getPlanningCycleId();

    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Please set your preferred context in the Planning Setup ',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const phases = await fetchPlanningJourneyPhases();
      this.setState({ phases });

      await this.loadManager(false);

      const requestedBudgetId = this.budgetId();
      if (requestedBudgetId > 0) {
        await this.loadWorkbench(requestedBudgetId, false);
      }
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private p = () => (this.props.params as any) || {};

  private budgetId = () =>
    Number(this.p().dbi ?? this.p().determinantbudgetid ?? 0);

  private setParam = (k: string, v: any) => this.props.onParam('set')(k, v);

  private loadManager = async (preserveMessage = true) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) return;

    this.setState({
      loading: true,
      error: '',
      warning: '',
      success: preserveMessage ? this.state.success : '',
    });

    try {
      const manager = await fetchDeterminantBudgetManager(planningcycleid);
      this.setState({ manager });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
      throw e;
    } finally {
      this.setState({ loading: false });
    }
  };

  private loadWorkbench = async (
    determinantbudgetid: number,
    preserveMessage = true,
  ) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid || !determinantbudgetid) return;

    this.setState({
      loading: true,
      error: '',
      success: preserveMessage ? this.state.success : '',
      warning: '',
    });

    try {
      const workbench = await fetchDeterminantBudgetWorkbench(
        planningcycleid,
        determinantbudgetid,
      );

      this.setState({
        workbench,
        tab: this.state.workbench
          ? this.state.tab
          : workbench.activeTab || 'summary',
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
      throw e;
    } finally {
      this.setState({ loading: false });
    }
  };

  private getWorkbench = async (preserve = false) => {
    const determinantbudgetid =
      this.state.workbench?.context.determinantbudgetid || this.budgetId();

    if (!determinantbudgetid) {
      await this.loadManager(preserve);
      return;
    }

    await this.loadWorkbench(determinantbudgetid, preserve);
  };

  private openBudget = async (budget: IDeterminantBudgetManagerRow) => {
    this.setParam('dbi', budget.rid);
    await this.loadWorkbench(budget.rid, false);
  };

  private returnToManager = async () => {
    this.setParam('dbi', null);
    this.setParam('determinantbudgetid', null);
    this.setState({
      workbench: null,
      tab: 'summary',
      success: '',
      warning: '',
      error: '',
    });
    await this.loadManager(false);
  };

  private refresh = async () => {
    if (this.state.workbench) {
      await this.getWorkbench(true);
    } else {
      await this.loadManager(true);
    }
  };

  private mutate = async (message: string, fn: () => Promise<any>) => {
    this.setState({ loading: true, error: '', success: '', warning: '' });
    try {
      await fn();
      this.setState({ success: message });
      await this.getWorkbench(true);
    } catch (e: any) {
      this.setState({ error: e?.message || String(e), loading: false });
    }
  };


  saveAdjustment = async () => {
    if (!this.state.adjustmentreason.trim()) {
      this.setState({ warning: 'A management adjustment reason is required.' });
      return;
    }
    const w = this.state.workbench!;
    await this.mutate('Management adjustment recorded.', () =>
      saveManagementAdjustment({
        determinantbudgetid: w.context.determinantbudgetid,
        //adjustmenttype: this.state.adjustmenttype,
        adjustmentamount: Number(this.state.adjustmentamount || 0),
        adjustmentreason: this.state.adjustmentreason.trim(),
        reserveamount: Number(this.state.reserveamount || 0), 
        //authoritycode: this.state.authoritycode || undefined,
        //stamp: w.context.stamp,
      })
    );
    this.setState({ adjustmentamount: '0', adjustmentreason: '' });
  };


  validate = () =>
    this.mutate('Determinant Budget validation completed.', () =>
      validateDeterminantBudget(
        this.state.workbench!.context.determinantbudgetid
      )
    );

  accept = () =>
    this.mutate('Determinant Budget accepted for release.', () =>
      acceptDeterminantBudget(
        this.state.workbench!.context.determinantbudgetid,
        this.state.notes
      )
    );

  returnBudget = () =>
    this.mutate('Determinant Budget returned to Executive Review.', () =>
      returnDeterminantBudget(
        this.state.workbench!.context.determinantbudgetid,
        this.state.notes
      )
    );

  release = async () => {
    const w = this.state.workbench!;
    await this.mutate('Planning capacity released.', () =>
      releaseDeterminantBudget(w.context.determinantbudgetid, this.state.notes)
    );
    const next = w.actions.nextpage || 'planning';
    this.setParam('planningcycleid', w.context.planningcycleid);
    this.setParam('dbi', w.context.determinantbudgetid);
    this.setParam('capacityversion', w.context.capacityversion);
    this.props.setPage(next);
  };

  renderContext() {
    const c = this.state.workbench?.context;
    if (!c) return null;
    return (
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption">Planning Cycle</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.planningcyclename || c.planningcyclecode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption">Executive Recommendation</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.executivereviewcode || c.executivereviewix || `#${c.executivereviewid}`}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption">Recommended Scenario</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.scenariocode} — {c.scenarioname}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 1.5 }}>
          <Chip size="small" label={c.statecode} />
        </Grid>
        <Grid size={{ xs: 6, md: 1.5 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`Capacity v${c.capacityversion}`}
          />
        </Grid>
      </Grid>
    );
  }

  money = (v: any) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
      Number(v || 0)
    );

  summary() {
    const w = this.state.workbench!;
    const cards = [
      ['Recommended Scenario', w.context.scenariocode],
      ['Economic Capacity', this.money(w.capacity.computedeconomiccapacity)],
      ['Planning Capacity', this.money(w.capacity.computedplanningcapacity)],
      ['Planning Envelope', this.money(w.capacity.planningenvelope)],
      ['Mandatory Report Readiness', `${w.report.mandatoryvalidationreadiness ?? w.report.completeness ?? 0}%`],
      [
        'Validation',
        w.validation.ready ? 'READY' : `${w.validation.blockingcount} blocking`,
      ],
    ];
    return (
      <Grid container spacing={2}>
        {cards.map(([l, v]) => (
          <Grid key={String(l)} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                {l}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {v}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  envelope() {
    const c = this.state.workbench!.capacity;
    const rows = [
      {
        id: 1,
        label: 'Computed Economic Capacity',
        amount: c.computedeconomiccapacity,
      },
      {
        id: 2,
        label: 'Computed Planning Capacity',
        amount: c.computedplanningcapacity,
      },
      { id: 3, label: 'Approved Adjustments', amount: c.approvedadjustment },
      { id: 4, label: 'Management Reserve', amount: c.managementreserve },
      { id: 5, label: 'Planning Envelope', amount: c.planningenvelope },
      { id: 6, label: 'Remaining Capacity', amount: c.remainingcapacity || 0 },
    ];
    return (
      <Stack spacing={2}>
        <Alert severity={c.stale ? 'warning' : 'info'}>
          {c.stale
            ? 'The inherited capacity is stale relative to its certified source and requires upstream correction.'
            : 'Economic capacity is inherited from the certified Executive Recommendation and is not recomputed in Determinant Budget.'}
        </Alert>
        <FGrid
          title="Budget Envelope"
          rows={rows}
          columns={[
            { field: 'label', headerName: 'Measure', flex: 1, minWidth: 280 },
            {
              field: 'amount',
              headerName: 'Amount',
              width: 220,
              renderCell: FGridMoneyCell,
            },
          ]}
          getRowId={(r: any) => r.id}
          height={420}
        />
      </Stack>
    );
  }

  capacity() {
    const rows = this.state.workbench!.capacitylines || [];
    return (
      <Stack spacing={1.5}>
        {rows.length === 0 && (
          <Alert severity="warning">
            Capacity Analysis detail is not present in the current workbench projection. The authoritative envelope remains available on the Budget Envelope tab.
          </Alert>
        )}
      <FGrid
        title="Capacity Analysis"
        rows={rows}
        columns={[
          { field: 'code', headerName: 'Code', width: 150 },
          {
            field: 'name',
            headerName: 'Capacity Component',
            flex: 1,
            minWidth: 260,
          },
          {
            field: 'source',
            headerName: 'Source',
            width: 180,
          },
          {
            field: 'computed',
            headerName: 'Computed',
            width: 180,
            renderCell: FGridMoneyCell,
          },
          {
            field: 'adjusted',
            headerName: 'Adjusted',
            width: 180,
            renderCell: FGridMoneyCell,
          },
          {
            field: 'final',
            headerName: 'Final',
            width: 180,
            renderCell: FGridMoneyCell,
          },
        ]}
        getRowId={(r: any) => r.code}
        height={520}
      />
      </Stack>
    );
  }

  management() {
    const w = this.state.workbench!;
    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Management Review does not re-select or recompute the Executive Review scenario.
          It records the authorized planning adjustment and/or management reserve applied to the certified economic capacity.
        </Alert>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Management Adjustment (+ / -)"
              type="number"
              value={this.state.adjustmentamount}
              disabled={Boolean(w.actions.readOnly || w.context.readonly)}
              onChange={(e) => this.setState({ adjustmentamount: e.target.value })}
              helperText="Positive increases the envelope; negative reduces it."
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Management Reserve"
              type="number"
              value={this.state.reserveamount}
              disabled={Boolean(w.actions.readOnly || w.context.readonly)}
              onChange={(e) => this.setState({ reserveamount: e.target.value })}
              helperText="Reserve is withheld from the planning envelope."
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Management Decision Reason"
              value={this.state.adjustmentreason}
              disabled={Boolean(w.actions.readOnly || w.context.readonly)}
              onChange={(e) => this.setState({ adjustmentreason: e.target.value })}
              multiline
              minRows={3}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              disabled={!w.actions.canAdjust || this.state.loading}
              onClick={this.saveAdjustment}
            >
              Record Management Decision
            </Button>
          </Grid>
        </Grid>
        <FGrid
          title="Management Decision History"
          rows={w.adjustments || []}
          columns={[
            { field: 'adjustmenttype', headerName: 'Type', width: 140 },
            { field: 'amount', headerName: 'Amount', width: 180, renderCell: FGridMoneyCell },
            { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 300 },
            { field: 'stamp', headerName: 'Updated', width: 190 },
          ]}
          getRowId={(r: any) => r.rid}
          height={340}
        />
      </Stack>
    );
  }

  validation() {
    const v = this.state.workbench!.validation;
    return (
      <Stack spacing={1.5}>
        <Alert severity={v.ready ? 'success' : v.stale ? 'warning' : 'error'}>
          Readiness score: {v.score || 0}% — {v.blockingcount || 0} blocking,{' '}
          {v.warningcount || 0} warnings.
        </Alert>
        {(v.items || []).map((x) => (
          <Alert
            key={x.code}
            severity={
              x.passed
                ? 'success'
                : x.severity === 'BLOCKING'
                  ? 'error'
                  : x.severity === 'WARNING'
                    ? 'warning'
                    : 'info'
            }
          >
            {x.label}
            {x.message ? `: ${x.message}` : ''}
          </Alert>
        ))}
        <Button
          variant="contained"
          startIcon={<FactCheckIcon />}
          disabled={
            !this.state.workbench!.actions.canValidate || this.state.loading
          }
          onClick={this.validate}
        >
          Run Validation
        </Button>
      </Stack>
    );
  }

  releaseTab() {
    const w = this.state.workbench!;
    return (
      <Stack spacing={2}>
        <Alert severity={w.context.statecode === 'RELEASED_TO_PLANNING' ? 'success' : w.actions.canRelease ? 'success' : 'warning'}>
          {w.context.statecode === 'RELEASED_TO_PLANNING'
            ? 'Planning capacity has been released. This completed Determinant Budget is retained as a read-only governance record.'
            : w.actions.canRelease
              ? 'All release gates are satisfied.'
              : 'Release requires acceptance and completion of the governed Determinant Report / validation gates.'}
        </Alert>
        <TextField
          label="Management / Release Notes"
          value={this.state.notes}
          onChange={(e) => this.setState({ notes: e.target.value })}
          multiline
          minRows={4}
          fullWidth
          disabled={Boolean(w.actions.readOnly || w.context.readonly)}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            color="success"
            disabled={!w.actions.canAccept || this.state.loading}
            onClick={this.accept}
          >
            Accept Determinant Budget
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!w.actions.canRelease || this.state.loading}
            onClick={this.release}
          >
            Release Planning Capacity
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ReplayIcon />}
            disabled={
              !w.actions.canReturn ||
              this.state.loading ||
              !this.state.notes.trim()
            }
            onClick={this.returnBudget}
          >
            Return to Executive Review
          </Button>
        </Stack>
      </Stack>
    );
  }

  renderTab() {
    const w = this.state.workbench;
    if (!w) return null;
    switch (this.state.tab) {
      case 'envelope':
        return this.envelope();
      case 'capacity':
        return this.capacity();
      case 'management':
        return this.management();
      case 'validation':
        return this.validation();
      case 'release':
        return this.releaseTab();
      case 'report':
        return (
          <DeterminantReportPanel
            determinantbudgetid={w.context.determinantbudgetid}
            report={w.report}
            loading={this.state.loading}
            canGenerate={w.actions.canRegenerateTables}
            readOnly={Boolean(w.actions.readOnly || w.context.readonly)}
            onReload={() => this.getWorkbench(true)}
            onError={(message) => this.setState({ error: message })}
            onSuccess={(message) => this.setState({ success: message, error: '' })}
          />
        );
      default:
        return (
          <Stack spacing={2}>
            {this.summary()}
            <Alert severity="info">
              The Determinant Budget consumes the active certified Executive
              Recommendation, establishes the governed planning envelope and produces
              Consolidated Report Tables 4–18 for planner validation and certification.
            </Alert>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Recommendation provenance
              </Typography>
              <Typography>
                {w.recommendation.reason || 'No rationale supplied.'}
              </Typography>
              {w.recommendation.conditions && (
                <Typography color="text.secondary">
                  Conditions: {w.recommendation.conditions}
                </Typography>
              )}
            </Paper>
          </Stack>
        );
    }
  }
  
  render() {
    const w = this.state.workbench;
    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            currentPhaseCode={w?.context.activephase || w?.context.phasecode || this.state.manager?.context.phasecode || 'DETERMINANT_BUDGET'}
          />
        }
        
        title="BIE Determinant Budget"
        subtitle="Initial planning capacity and Consolidated Report Tables 4–18"
        icon={<AccountBalanceIcon />}
        loading={this.state.loading}
        onRefresh={this.refresh}
        //context={this.renderContext()}
      >
        <Stack spacing={2}>
          {this.state.error && (
            <Alert severity="error">{this.state.error}</Alert>
          )}
          {this.state.success && (
            <Alert severity="success">{this.state.success}</Alert>
          )}
          {this.state.warning && (
            <Alert severity="warning">{this.state.warning}</Alert>
          )}
          {!w && this.state.manager && (
            <DeterminantBudgetManager
              manager={this.state.manager}
              loading={this.state.loading}
              onRefresh={() => this.loadManager(true)}
              onOpen={this.openBudget}
            />
          )}
          {w && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" onClick={this.returnToManager}>
                Determinant Budget Manager
              </Button>
              <Chip
                size="small"
                variant="outlined"
                label={w.context.determinantbudgetcode || `DB #${w.context.determinantbudgetid}`}
              />
            </Stack>
          )}
          {w?.context.phasecontextwarning && (
            <Alert severity="info">
              {w.context.phasecontextwarning} This workspace is read-only; use Budget Control for the active planning stage.
            </Alert>
          )}
          {w && (
            <Tabs
              value={this.state.tab}
              onChange={(_, tab) => this.setState({ tab })}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="summary" label="Executive Summary" />
              <Tab value="envelope" label="Budget Envelope" />
              <Tab value="capacity" label="Capacity Analysis" />
              <Tab value="management" label="Management Review" />
              <Tab value="report" label="Consolidated Report (Tables 4–18)" />
              <Tab
                value="validation"
                label={`Structural Validation (${w.validation.blockingcount})`}
              />
              <Tab value="release" label="Release" />
            </Tabs>
          )}
          <Box>{this.renderTab()}</Box>
        </Stack>
      </PlanningWorkbenchShell>
    );
  }
}
export default DeterminantBudgetWorkbench;
